import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import {
  STATUS_COLORS, STATUSES, ColorCodingLegend,
  ADVANCE_COLORS, ADVANCE_STATUS_KEYS, AdvanceColorLegend,
  AUTO_COLORS, AutoStatusLegend,
} from './hrShared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();

// Cycle order for non-advance tabs: blank → DONE → INPROCESS → PENDING → HOLD → CANCEL → blank
const CYCLE = ['', ...STATUSES];
const cycleNext = (cur) => {
  const i = CYCLE.indexOf((cur || '').toUpperCase());
  return CYCLE[(i + 1) % CYCLE.length];
};

/**
 * Generic Excel-style HR grid: employees as ROWS × dates as COLUMNS.
 * Used by 6 HR Reports sub-tabs (Staff Advance Work / Daily Work Report /
 * Daily Schedule Report / Staff Report Index / Daily Work / Daily Schedule).
 *
 * useColorPicker=true → Staff Advance Work mode (% color picker popup)
 * useAutoStatus=true  → Daily Work/Schedule mode (auto DONE/ABSENT/PENDING, read-only)
 */
const HRGridTab = ({ tab, title, titleBg = '#1e3a8a', titleFg = '#ffffff', useColorPicker = false, useAutoStatus = false }) => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  // Color picker popup state: { empName, day, top, left } | null
  const [picker, setPicker] = useState(null);
  const pickerRef = useRef(null);

  const days = useMemo(() => getDaysInMonth(month, year), [month, year]);
  const dayNumbers = useMemo(() => Array.from({ length: days }, (_, i) => i + 1), [days]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = useAutoStatus ? `${API}/hr/grid-entries-auto` : `${API}/hr/grid-entries`;
      const res = await axios.get(endpoint, {
        params: { tab, month, year },
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
      });
      setRecords(res.data?.records || []);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [tab, month, year, useAutoStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Close picker on outside click
  useEffect(() => {
    if (!picker) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPicker(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [picker]);

  const updateDay = async (employeeName, day, newStatus) => {
    setRecords(prev =>
      prev.map(r =>
        r.employeeName === employeeName
          ? { ...r, days: { ...(r.days || {}), [day]: newStatus } }
          : r
      )
    );
    setPicker(null);
    try {
      await axios.patch(`${API}/hr/grid-entries/update-day`,
        { tab, employeeName, month, year, day, status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` } }
      );
    } catch {
      fetchData();
    }
  };

  const updateRemark = async (employeeName, remark) => {
    setRecords(prev =>
      prev.map(r => (r.employeeName === employeeName ? { ...r, remark } : r))
    );
    try {
      await axios.patch(`${API}/hr/grid-entries/update-day`,
        { tab, employeeName, month, year, remark },
        { headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` } }
      );
    } catch { /* silent */ }
  };

  // Live counts
  const counts = useMemo(() => {
    if (useAutoStatus) {
      const c = { DONE: 0, ABSENT: 0, PENDING: 0 };
      records.forEach(r => {
        Object.values(r.days || {}).forEach(v => {
          const k = (v || '').toUpperCase();
          if (c[k] !== undefined) c[k]++;
        });
      });
      return c;
    }
    if (useColorPicker) {
      const c = {};
      ADVANCE_STATUS_KEYS.forEach(k => { c[k] = 0; });
      records.forEach(r => {
        Object.values(r.days || {}).forEach(v => {
          const k = (v || '').toUpperCase();
          if (c[k] !== undefined) c[k]++;
        });
      });
      return c;
    }
    const c = { DONE: 0, INPROCESS: 0, PENDING: 0, HOLD: 0, CANCEL: 0 };
    records.forEach(r => {
      Object.values(r.days || {}).forEach(v => {
        const k = (v || '').toUpperCase();
        if (c[k] !== undefined) c[k]++;
      });
    });
    return c;
  }, [records, useColorPicker, useAutoStatus]);

  const goPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const handleCellClick = (e, empName, day, cur) => {
    if (useColorPicker) {
      const rect = e.currentTarget.getBoundingClientRect();
      const POPUP_H = 290; // approx popup height (5 options + clear + title)
      const POPUP_W = 168;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Flip upward if not enough space below
      const top = rect.bottom + POPUP_H > vh
        ? Math.max(4, rect.top - POPUP_H)
        : rect.bottom + 2;

      // Keep within horizontal bounds
      const left = rect.left + POPUP_W > vw
        ? Math.max(4, vw - POPUP_W - 8)
        : rect.left;

      setPicker({ empName, day, top, left });
    } else {
      updateDay(empName, day, cycleNext(cur));
    }
  };

  return (
    <div data-testid={`hr-grid-${tab}`} style={{ position: 'relative' }}>
      {/* Title banner */}
      <div
        className="px-3 py-2 rounded-t font-bold text-sm flex items-center justify-between"
        style={{ backgroundColor: titleBg, color: titleFg }}
      >
        <span className="tracking-wide">{title}</span>
        <span className="text-[11px] opacity-90">EMPLOYEE × DATE GRID</span>
      </div>

      <Card className="rounded-t-none">
        <CardContent className="p-3">
          {/* Top controls: month nav */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <button onClick={goPrevMonth} data-testid={`grid-${tab}-prev-month`}
              className="p-1 rounded border border-stone-300 hover:bg-stone-100">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))}
              data-testid={`grid-${tab}-month-select`}
              className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}
              data-testid={`grid-${tab}-year-select`}
              className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">
              {[2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button onClick={goNextMonth} data-testid={`grid-${tab}-next-month`}
              className="p-1 rounded border border-stone-300 hover:bg-stone-100">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <span className="ml-2 text-[11px] text-stone-500">
              {records.length} employees · {days} days
            </span>
            {useAutoStatus && (
              <button onClick={fetchData} className="ml-auto flex items-center gap-1 px-2 py-1 text-[11px] text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-50 transition-colors" title="Refresh auto-sync">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            )}
          </div>

          {/* Legend */}
          {useAutoStatus
            ? <AutoStatusLegend counts={counts} />
            : useColorPicker
              ? <AdvanceColorLegend counts={counts} />
              : <ColorCodingLegend counts={counts} />
          }

          <div className="text-[10px] text-stone-500 mb-1.5">
            {useAutoStatus
              ? <span className="font-medium text-emerald-700">Auto-synced from Index 1&2 submissions · <span className="text-stone-400">Read-only — updates automatically when employees fill their daily work</span></span>
              : useColorPicker
                ? 'Click a day cell to open color picker (100% · 75% · 50% · 20% · 0%)'
                : <>Click a day cell to cycle: <span className="font-semibold">— → DONE → INPROCESS → PENDING → HOLD → CANCEL → —</span></>
            }
          </div>

          {/* Grid table */}
          <div
            className="overflow-auto border border-stone-300 rounded"
            style={{ maxHeight: 560 }}
            data-testid={`grid-${tab}-scroll`}
          >
            <table className="text-[11px] border-collapse" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-20">
                <tr style={{ backgroundColor: titleBg, color: titleFg }}>
                  <th className="px-1.5 py-1 text-center font-bold border border-stone-700 sticky left-0 z-30"
                    style={{ width: 38, backgroundColor: titleBg }}>SR</th>
                  <th className="px-2 py-1 text-left font-bold border border-stone-700 sticky z-30"
                    style={{ width: 170, left: 38, backgroundColor: titleBg }}>EMPLOYEE NAME</th>
                  <th className="px-1.5 py-1 text-left font-bold border border-stone-700"
                    style={{ width: 100 }}>DEPT</th>
                  {dayNumbers.map(d => (
                    <th key={d} className="text-center font-bold border border-stone-700" style={{ width: 28 }}>{d}</th>
                  ))}
                  <th className="px-1.5 py-1 text-center font-bold border border-stone-700" style={{ width: 130 }}>REMARK</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={dayNumbers.length + 4} className="py-6 text-center text-stone-400 text-xs">Loading…</td></tr>
                )}
                {!loading && records.length === 0 && (
                  <tr><td colSpan={dayNumbers.length + 4} className="py-6 text-center text-stone-400 text-xs">No employees found.</td></tr>
                )}
                {!loading && records.map((r, i) => {
                  const rowBg = i % 2 === 0 ? '#ffffff' : '#fafaf9';
                  return (
                    <tr key={r.employeeName + i} className="hover:bg-blue-50/40">
                      <td className="px-1.5 py-1 text-center font-semibold border border-stone-200 sticky left-0 z-10"
                        style={{ backgroundColor: rowBg, width: 38 }}>{r.srNo || i + 1}</td>
                      <td className="px-2 py-1 text-left font-semibold border border-stone-200 sticky z-10 truncate"
                        style={{ left: 38, backgroundColor: rowBg, width: 170, maxWidth: 170 }}
                        title={r.employeeName}>{r.employeeName}</td>
                      <td className="px-1.5 py-1 text-left text-stone-600 border border-stone-200 truncate"
                        style={{ width: 100, maxWidth: 100 }}
                        title={r.department || ''}>{r.department || ''}</td>
                      {dayNumbers.map(d => {
                        const cur = ((r.days || {})[d] || (r.days || {})[String(d)] || '').toUpperCase();
                        const colorInfo = useAutoStatus
                          ? AUTO_COLORS[cur]
                          : useColorPicker
                            ? ADVANCE_COLORS[cur]
                            : STATUS_COLORS[cur];
                        // Auto-status: show 1st letter of status; no click for auto mode
                        const cellLabel = useAutoStatus
                          ? (colorInfo ? cur[0] : '')           // D / A / P
                          : useColorPicker
                            ? (colorInfo ? colorInfo.label : '')
                            : (cur ? cur[0] : '');
                        return (
                          <td
                            key={d}
                            onClick={useAutoStatus ? undefined : e => handleCellClick(e, r.employeeName, d, cur)}
                            className={`text-center border border-stone-200 select-none ${useAutoStatus ? 'cursor-default' : 'cursor-pointer'}`}
                            style={{
                              width: 28,
                              backgroundColor: colorInfo ? colorInfo.bg : '#ffffff',
                              color: colorInfo ? colorInfo.fg : '#a8a29e',
                              fontWeight: colorInfo ? 800 : 400,
                              fontSize: 10,
                              padding: '4px 0',
                            }}
                            title={useAutoStatus
                              ? (colorInfo ? colorInfo.label : '—')
                              : useColorPicker
                                ? (colorInfo ? colorInfo.label : 'Click to set color')
                                : (cur || 'Click to set status')
                            }
                            data-testid={`grid-${tab}-cell-${i}-${d}`}
                          >
                            {cellLabel}
                          </td>
                        );
                      })}
                      <td className="border border-stone-200 p-0" style={{ width: 130 }}>
                        <input
                          defaultValue={r.remark || ''}
                          onBlur={e => {
                            if (e.target.value !== (r.remark || '')) {
                              updateRemark(r.employeeName, e.target.value);
                            }
                          }}
                          className="w-full px-1.5 py-1 text-[10px] focus:outline-none focus:bg-yellow-50 bg-transparent"
                          placeholder="—"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Color Picker Popup ── */}
      {picker && (
        <div
          ref={pickerRef}
          style={{
            position: 'fixed',
            top: picker.top,
            left: picker.left,
            zIndex: 9999,
          }}
          data-testid="advance-color-picker-popup"
        >
          <div className="bg-white border border-stone-300 rounded-xl shadow-2xl p-3 flex flex-col gap-1.5" style={{ minWidth: 160 }}>
            <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wide mb-1">Choose Completion %</p>
            {ADVANCE_STATUS_KEYS.map(key => {
              const c = ADVANCE_COLORS[key];
              return (
                <button
                  key={key}
                  onClick={() => updateDay(picker.empName, picker.day, key)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:opacity-90 transition-opacity w-full text-left font-bold text-sm"
                  style={{ backgroundColor: c.bg, color: c.fg }}
                  data-testid={`pick-color-${key}`}
                >
                  {c.label}
                </button>
              );
            })}
            <button
              onClick={() => updateDay(picker.empName, picker.day, '')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors w-full text-left mt-0.5 text-xs font-semibold text-stone-500"
              data-testid="pick-color-clear"
            >
              ✕ Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRGridTab;
